import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Globe,
  Laptop,
  Network,
  Server,
  Settings,
  Shield,
  Star,
  Tv,
  Wifi,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  {
    icon: Network,
    title: 'Networking',
    color: 'from-sky-500 to-cyan-400',
    description: 'Reliable wired and wireless networks designed for stable day-to-day performance.',
  },
  {
    icon: Laptop,
    title: 'IT Support',
    color: 'from-fuchsia-500 to-rose-400',
    description: 'Fast troubleshooting and practical support to keep your setup moving.',
  },
  {
    icon: Wifi,
    title: 'Starlink Installation',
    color: 'from-blue-600 to-sky-400',
    description: 'Professional remote-connectivity installs with clean, secure setup.',
  },
  {
    icon: Globe,
    title: 'Web Development',
    color: 'from-emerald-500 to-lime-400',
    description: 'Modern websites that help businesses look credible and get found online.',
  },
  {
    icon: Shield,
    title: 'Security Systems',
    color: 'from-orange-500 to-red-400',
    description: 'Practical protection for homes and businesses with dependable hardware.',
  },
  {
    icon: Tv,
    title: 'TV Wall Mounting',
    color: 'from-slate-700 to-slate-500',
    description: 'Clean installs with thoughtful placement, cabling, and finishing.',
  },
  {
    icon: Settings,
    title: 'System Admin',
    color: 'from-indigo-500 to-blue-400',
    description: 'Hands-on management for systems, accounts, backups, and maintenance.',
  },
  {
    icon: Server,
    title: 'Cloud Services',
    color: 'from-teal-500 to-emerald-400',
    description: 'Flexible cloud solutions that scale with your business needs.',
  },
];

const highlights = [
  {
    icon: Clock3,
    title: 'Fast Regional Response',
    description: 'Practical support across multiple service areas that is easy to book.',
  },
  {
    icon: Shield,
    title: 'Built For Reliability',
    description: 'Solutions focused on stability, security, and fewer headaches later.',
  },
  {
    icon: CheckCircle2,
    title: 'Clear Communication',
    description: 'Straightforward advice without the jargon spiral.',
  },
];

const steps = [
  {
    number: '01',
    title: 'Tell Us What You Need',
    description: 'Share the issue, project, or service you want help with.',
  },
  {
    number: '02',
    title: 'We Map The Right Fix',
    description: 'We recommend the best-fit solution for your setup and budget.',
  },
  {
    number: '03',
    title: 'Get It Done Properly',
    description: 'Installation, support, and follow-through all handled with care.',
  },
];

const stats = [
  { value: '8+', label: 'core service areas' },
  { value: '3', label: 'simple steps to get started' },
  { value: 'Multi-city', label: 'coverage across service regions' },
];

const marqueeServices = [...services, ...services];

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const animationRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const savedPos = useRef(0);
  const isPaused = useRef(false);
  const resumeTimer = useRef(null);
  const posRef = useRef(0);
  const trackRef = useRef(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const speed = 0.45;

    const animate = () => {
      if (!isPaused.current) {
        posRef.current += speed;
        const halfWidth = track.scrollWidth / 2;
        if (posRef.current >= halfWidth) posRef.current = 0;
        track.style.transform = `translateX(-${posRef.current}px)`;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
      clearTimeout(resumeTimer.current);
    };
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const updateTrackPosition = (next) => {
    const track = trackRef.current;
    if (!track) return;

    const halfWidth = track.scrollWidth / 2;
    const bounded = Math.max(0, Math.min(next, halfWidth - 1));
    posRef.current = bounded;
    track.style.transform = `translateX(-${bounded}px)`;
  };

  const pauseScroll = () => {
    isPaused.current = true;
    clearTimeout(resumeTimer.current);
  };

  const resumeScroll = () => {
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => {
      isPaused.current = false;
    }, 1200);
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    pauseScroll();
    startX.current = e.pageX;
    savedPos.current = posRef.current;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.pageX - startX.current;
    updateTrackPosition(savedPos.current - dx);
  };

  const handleMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    resumeScroll();
  };

  const handleTouchStart = (e) => {
    isDragging.current = true;
    pauseScroll();
    startX.current = e.touches[0].pageX;
    savedPos.current = posRef.current;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.touches[0].pageX - startX.current;
    updateTrackPosition(savedPos.current - dx);
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    resumeScroll();
  };

  return (
    <div className="overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white">
      <section
        className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.8),transparent_45%)] dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_35%)]" />
        </div>

        <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
              Support for homes and businesses across multiple cities
            </div>
            <h1
              className="max-w-3xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-7xl dark:text-white"
              data-testid="hero-title"
            >
              Tech support that feels
              <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                clear, flexible, and dependable.
              </span>
            </h1>
            <p
              className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300"
              data-testid="hero-subtitle"
            >
              Connectify helps with networking, IT support, Starlink installs, security systems,
              websites, and the practical setup work that keeps everything running smoothly across
              your service regions.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-7 text-slate-500 dark:text-slate-400">
              Supporting clients in Tasmania, Darwin, Canberra, and broader Australian service
              regions.
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/booking"
                data-testid="hero-booking-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-[#1E90FF] px-8 py-4 font-semibold text-white shadow-[0_18px_45px_-18px_rgba(30,144,255,0.45)] transition duration-300 hover:-translate-y-1 hover:bg-[#1570d6] hover:shadow-[0_20px_50px_-20px_rgba(30,144,255,0.55)] dark:bg-[#1E90FF] dark:text-white dark:hover:bg-[#1570d6]"
              >
                <CalendarDays className="h-5 w-5" />
                <span>Book a Service</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white/80 px-8 py-4 font-semibold text-slate-800 backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-slate-900 hover:text-slate-950 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:border-cyan-300 dark:hover:text-cyan-200"
              >
                Explore Services
              </Link>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.4)] backdrop-blur dark:border-white/10 dark:bg-white/5"
                >
                  <div className="text-2xl font-black text-slate-950 dark:text-white">{stat.value}</div>
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-400/20 via-cyan-300/10 to-emerald-300/20 blur-2xl dark:from-sky-400/15 dark:to-emerald-400/10" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-slate-950 p-8 text-white shadow-[0_30px_80px_-24px_rgba(2,12,27,0.7)] dark:border-white/10">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <p className="text-sm uppercase tracking-[0.25em] text-sky-200/80">Connectify</p>
                  <h2 className="mt-2 text-2xl font-bold">What we handle best</h2>
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-sky-100">
                  Multi-city support
                </div>
              </div>

              <div className="mt-6 grid gap-4">
                {highlights.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur"
                  >
                    <div className="flex items-start gap-4">
                      <div className="rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-300 p-3 text-slate-950">
                        <item.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">{item.title}</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-300">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-5 text-sm text-emerald-100">
                From first booking to final install, the goal is simple: fewer tech problems, less
                confusion, and a setup you can trust.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="services-overview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
                Service Areas
              </p>
              <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
                Practical help across the work that keeps your systems connected.
              </h2>
            </div>
            <p className="max-w-xl text-base leading-7 text-slate-600 dark:text-slate-300">
              Drag through the services below to get a quick feel for what Connectify can take care
              of, from home installs to business IT foundations.
            </p>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-slate-200/70 bg-white/75 p-4 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.35)] backdrop-blur dark:border-white/10 dark:bg-white/5">
            <div
              ref={trackRef}
              className="flex w-max gap-6 cursor-grab select-none active:cursor-grabbing"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {marqueeServices.map((service, index) => (
                <div
                  key={`${service.title}-${index}`}
                  className="group flex w-72 flex-shrink-0 flex-col rounded-[1.75rem] border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.95))] p-6 transition duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_24px_65px_-28px_rgba(14,165,233,0.55)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.88),rgba(15,23,42,0.58))] dark:hover:border-cyan-300/60 dark:hover:shadow-[0_24px_65px_-28px_rgba(34,211,238,0.35)]"
                >
                  <div className={`flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${service.color} shadow-lg`}>
                    <service.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold text-slate-950 transition-colors group-hover:text-sky-700 dark:text-white dark:group-hover:text-cyan-300">
                    {service.title}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {service.description}
                  </p>
                  <Link
                    to={`/booking?service=${encodeURIComponent(service.title)}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 transition hover:gap-3 dark:text-cyan-300"
                  >
                    Request this service
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="inline-flex items-center gap-2 text-base font-semibold text-sky-700 hover:underline dark:text-cyan-300"
            >
              <span>View all services</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200/70 bg-slate-950 py-20 text-white dark:border-white/10" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              How It Works
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              A simple process that gets you from problem to solution fast.
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {steps.map((step) => (
              <div
                key={step.number}
                className="rounded-[1.75rem] border border-white/10 bg-white/5 p-8 backdrop-blur"
              >
                <div className="text-sm font-semibold tracking-[0.25em] text-cyan-300">{step.number}</div>
                <h3 className="mt-5 text-2xl font-bold">{step.title}</h3>
                <p className="mt-4 text-sm leading-7 text-slate-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="testimonials-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
                Testimonials
              </p>
              <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
                People remember how smoothly the job went.
              </h2>
            </div>
            <p className="max-w-xl text-base leading-7 text-slate-600 dark:text-slate-300">
              The homepage still pulls live testimonial data, so this section updates as new reviews
              are added.
            </p>
          </div>

          {testimonials.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-3">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  data-testid={`testimonial-card-${index}`}
                  className="rounded-[1.75rem] border border-slate-200 bg-white p-8 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.35)] transition duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5"
                >
                  <div className="mb-5 flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-current text-amber-400" />
                    ))}
                  </div>
                  <p className="text-base leading-7 text-slate-700 dark:text-slate-200">
                    "{testimonial.message}"
                  </p>
                  <p className="mt-6 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
                    {testimonial.name}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[1.75rem] border border-dashed border-slate-300 bg-white/70 px-6 py-12 text-center text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
              Be the first to share your experience.
            </div>
          )}

          <div className="mt-8 text-center">
            <Link
              to="/testimonials"
              data-testid="view-all-testimonials-btn"
              className="inline-flex items-center gap-2 text-base font-semibold text-sky-700 hover:underline dark:text-cyan-300"
            >
              <span>View all testimonials</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-20 pt-6" data-testid="cta-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] bg-[linear-gradient(135deg,#0f172a_0%,#0f766e_100%)] p-10 text-white shadow-[0_35px_90px_-35px_rgba(15,23,42,0.8)] sm:p-14">
            <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="max-w-2xl">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200">
                  Ready To Start
                </p>
                <h2 className="mt-4 text-3xl font-black sm:text-4xl">
                  Book your service and get the right fix underway.
                </h2>
                <p className="mt-4 text-base leading-7 text-slate-200">
                  Whether you need a one-off install or ongoing support, Connectify can help you get
                  sorted without the usual stress.
                </p>
              </div>

              <Link
                to="/booking"
                data-testid="cta-booking-btn"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 font-semibold text-slate-950 transition duration-300 hover:-translate-y-1 hover:bg-slate-100"
              >
                <span>Book now</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
